import { Controller } from "@hotwired/stimulus";
import RequestHelper from "../helpers/request_helper";

const SLICK_ARROW_LEFT = "<button type=\"button\" class=\"slick-prev slick-arrow pill\"><i aria-hidden=\"true\" class=\"icon-lasso-chevron-left\"></i></button>";
const SLICK_ARROW_RIGHT = "<button type=\"button\" class=\"slick-next slick-arrow pill\"><i aria-hidden=\"true\" class=\"icon-lasso-chevron-right\"></i></button>";

const LOADING_PRODUCT_DETAIL_HTML = `
  <div class="product-details-loading">
    <div class="loading-images-column">
      <div class="loading-thumbs">
        <div class="loading-thumb product-modal-shimmer"></div>
        <div class="loading-thumb product-modal-shimmer"></div>
        <div class="loading-thumb product-modal-shimmer"></div>
      </div>
      <div class="loading-fullsize product-modal-shimmer"></div>
    </div>
    <div class="loading-info-column">
      <div class="loading-title product-modal-shimmer"></div>
      <div class="loading-tags product-modal-shimmer"></div>
      <div class="loading-summary product-modal-shimmer"></div>
      <div class="loading-line product-modal-shimmer"></div>
      <div class="loading-select product-modal-shimmer"></div>
      <div class="loading-desc product-modal-shimmer"></div>
      <div class="loading-desc product-modal-shimmer"></div>
      <div class="loading-desc product-modal-shimmer"></div>
    </div>
  </div>
`;

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 992;

// Grid rendering and infinite scroll. Local to this controller;
// collection_detail_products_controller.js carries its own equivalent.
class ProductGridService {
  static addToColumns(grid, nodes, startIndex, columnCount) {
    if (!grid) { return; }
    const columns = grid.querySelectorAll(".generated-products-column");
    nodes.forEach((node, i) => {
      const column = columns[(startIndex + i) % columnCount];
      if (column) { column.appendChild(node); }
    });
  }

  // Intentionally empty: loading-card placeholders have never rendered here. The old
  // implementation looked up a template path that did not exist.
  static addLoadingCards() {}

  static removeLoadingCards() {}
}

// Paired with collection_generator_prompt_controller.js. Unlike
// collection_detail_products_controller.js, which mounts on an already-known collection, this one
// does not auto-load: it stays idle until the prompt controller calls `loadCollection`.
export default class extends Controller {
  static get targets() {
    return ["productTemplate"];
  }

  static get values() {
    return { generatorUrl: String };
  }

  connect() {
    this.products = [];
    this.loading = false;
    this.currentCollectionId = null;
    this.currentPage = 0;
    this.totalPages = 0;
    this.currentProductIndex = 0;

    this.generatorPath = this.generatorUrlValue;
    this.basePath = this.generatorPath.substring(0, this.generatorPath.lastIndexOf("/"));

    // These stay plain lookups, not Targets: the grid and modal markup below come from
    // gives/shared/_product_columns and gives/shared/_product_details_carousel_modal, both of
    // which are also rendered by other clusters' views (gives/collections/previews, and
    // subdomain/collections/show for the modal) and by collection_detail_products_controller.js's
    // own mount. Adding this controller's Target attributes to that shared markup would do
    // nothing for those other mounts while coupling markup none of them own to this cluster.
    this.desktopGrid = this.element.querySelector(".desktop-products-grid");
    this.mobileGrid = this.element.querySelector(".mobile-products-grid");
    this.modalEl = this.element.querySelector("#product-detail-modal");
    this.carouselEl = this.modalEl ? this.modalEl.querySelector(".product-details-carousel") : null;

    // The scroll listener stays manual: it's bound partly to `window`, which a data-action
    // can't reach, and partly to the shared grid markup noted above. Both media-query listeners
    // are on MediaQueryList objects, which have no markup element to hang a data-action off at
    // all.
    this.scrollHandler = this.handleScroll.bind(this);
    this.refreshSlickHandler = this.refreshSlick.bind(this);
    this.desktopGrid?.addEventListener("scroll", this.scrollHandler);
    window.addEventListener("scroll", this.scrollHandler);

    this.mobileMediaQuery = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT}px)`);
    this.tabletMediaQuery = window.matchMedia(`(min-width: ${TABLET_BREAKPOINT}px)`);
    this.mobileMediaQuery.addEventListener("change", this.refreshSlickHandler);
    this.tabletMediaQuery.addEventListener("change", this.refreshSlickHandler);

    // Bootstrap's modal plugin announces `shown`/`hidden` through jQuery's event system only, and
    // everything downstream in this file that touches the carousel is slick-owned markup, so the
    // jQuery wrapper stays for the whole modal/carousel lifecycle below (open, teardown, and the
    // slide contents) rather than converting only some of these calls to native.
    if (this.modalEl) {
      $(this.modalEl).on("shown.bs.modal.collectionGeneratorProducts", () => this.onModalShown());
      $(this.modalEl).on("hidden.bs.modal.collectionGeneratorProducts", () => this.onModalHidden());
    }
  }

  disconnect() {
    this.desktopGrid?.removeEventListener("scroll", this.scrollHandler);
    window.removeEventListener("scroll", this.scrollHandler);
    this.mobileMediaQuery?.removeEventListener("change", this.refreshSlickHandler);
    this.tabletMediaQuery?.removeEventListener("change", this.refreshSlickHandler);
    if (this.modalEl) { $(this.modalEl).off(".collectionGeneratorProducts"); }
    if (this.carouselEl && $(this.carouselEl).hasClass("slick-initialized")) {
      $(this.carouselEl).off("afterChange");
      $(this.carouselEl).slick("unslick");
    }
  }

  // This mount is a descendant of the prompt controller's, so its instance is reached by walking
  // up to the element that actually carries its `data-controller`, not via `this.element`.
  promptController() {
    const promptEl = this.element.closest('[data-controller~="collection-generator-prompt"]');
    return promptEl ? this.application.getControllerForElementAndIdentifier(promptEl, "collection-generator-prompt") : null;
  }

  // Entry point the prompt controller calls directly.
  loadCollection(collectionId) {
    if (this.currentCollectionId === collectionId) {
      this.promptController()?.onCollectionSkipped();
      return;
    }

    // `.generated-products-column` is the same shared-partial markup noted in connect() above, so
    // this stays a plain query rather than a Target too.
    this.element.querySelectorAll(".generated-products-column").forEach((el) => { el.innerHTML = ""; });
    this.products = [];

    this.loadPage(collectionId, 1);
  }

  buildUrl(collectionId, page) {
    return `${this.basePath}/${collectionId}?page=${page}`;
  }

  buildDetailUrl(product) {
    return `${this.basePath}/${this.currentCollectionId}/products/${product.id}?cta=products_grid`;
  }

  loadPage(collectionId, page) {
    this.loading = true;
    ProductGridService.addLoadingCards();

    const url = this.buildUrl(collectionId, page);

    fetch(url, RequestHelper.defaultRequestOptions())
      .then((response) => {
        if (!response.ok) {
          return response.json()
            .catch(() => ({}))
            .then((body) => { throw new Error(body?.error || "Failed to generate products for the collection."); });
        }
        return response.json();
      })
      .then((data) => {
        this.currentCollectionId = collectionId;
        this.currentPage = data.pagination.current_page;
        this.totalPages = data.pagination.total_pages;

        const isFirstPage = page === 1;
        const products = this.addApiProducts(data.products);
        this.products = isFirstPage ? products : this.products.concat(products);

        this.loading = false;
        ProductGridService.removeLoadingCards();

        this.promptController()?.onProductsPageLoaded({
          totalProducts: data.collection.total_products,
          isFirstPage
        });
      })
      .catch((error) => {
        window.ltAlert({ message: error.message || "Failed to generate products for the collection.", type: "toast-error", duration: 2500 });
        this.loading = false;
        ProductGridService.removeLoadingCards();

        this.promptController()?.onProductsLoadFailed();
      });
  }

  addApiProducts(products) {
    const startIndex = this.products.length;
    const desktopNodes = products.map((p, i) => this.buildProductCard(p, `product-${p.id}-desktop`, startIndex + i));
    const mobileNodes = products.map((p, i) => this.buildProductCard(p, `product-${p.id}-mobile`, startIndex + i));
    ProductGridService.addToColumns(this.desktopGrid, desktopNodes, startIndex, 3);
    ProductGridService.addToColumns(this.mobileGrid, mobileNodes, startIndex, 2);
    return products;
  }

  buildProductCard(product, containerId, productIndex) {
    const fragment = this.productTemplateTarget.content.cloneNode(true);
    const el = fragment.firstElementChild;
    el.id = containerId;
    // Read back below off `event.currentTarget`, not this controller's own root element -- the
    // Values API only expresses attributes on the controller's mount, not a per-card index on a
    // node the controller creates itself, so this stays a plain attribute.
    el.dataset.productIndex = productIndex;
    el.querySelector(".product-name").textContent = product.name;

    const carousel = el.querySelector(".carousel");
    (product.images || []).forEach((src) => {
      const imageEl = document.createElement("div");
      imageEl.className = "image";
      const img = document.createElement("img");
      img.className = "thumbnail-1";
      img.src = src;
      imageEl.appendChild(img);
      carousel.appendChild(imageEl);
    });

    // Deferred a frame so the caller's `addToColumns` (called right after this
    // returns) has appended the card first -- the same ordering the original got for free from
    // `$timeout(fn, 0)` running after the digest had already inserted the compiled node.
    if (product.images && product.images.length > 1) {
      requestAnimationFrame(() => this.initProductCarousel(el));
    }

    return el;
  }

  initProductCarousel(cardEl) {
    const $carousel = $(cardEl).find(".carousel");
    if ($carousel.length && $carousel.children().length > 0 && !$carousel.hasClass("slick-initialized")) {
      $carousel.slick({
        dots: false,
        infinite: true,
        speed: 300,
        slidesToShow: 1,
        adaptiveHeight: false,
        prevArrow: SLICK_ARROW_LEFT,
        nextArrow: SLICK_ARROW_RIGHT
      });
    }
  }

  // Shows the product details inside a modal
  expandProductDetails(event) {
    if (event.target.closest(".slick-arrow")) { return; }
    const index = parseInt(event.currentTarget.dataset.productIndex, 10);
    this.openModal(index);
  }

  openModal(index) {
    this.currentProductIndex = isNaN(index) ? 0 : index;
    if (this.modalEl) { $(this.modalEl).modal("show"); }
  }

  onModalShown() {
    const $carousel = $(this.carouselEl);
    $carousel.empty();
    this.products.forEach(() => $carousel.append("<div class=\"item\"></div>"));

    $carousel.slick({
      infinite: false,
      initialSlide: this.currentProductIndex,
      arrows: true,
      nextArrow: SLICK_ARROW_RIGHT,
      prevArrow: SLICK_ARROW_LEFT,
      responsive: [
        {
          breakpoint: 768,
          settings: { arrows: false }
        }
      ]
    });

    $carousel.off("afterChange").on("afterChange", (event, slick, currentSlide) => {
      this.currentProductIndex = currentSlide;

      this.insertProductDetails();

      if (this.currentProductIndex >= this.products.length - 3 && this.currentPage < this.totalPages) {
        this.loadPage(this.currentCollectionId, this.currentPage + 1);
      }
    });

    this.insertProductDetails();
  }

  onModalHidden() {
    const $carousel = $(this.carouselEl);
    if ($carousel.hasClass("slick-initialized")) {
      $carousel.off("afterChange");
      $carousel.slick("unslick");
    }
  }

  insertProductDetails(productIndex = this.currentProductIndex) {
    const $slide = $(this.carouselEl).find(".slick-slide").not(".slick-cloned").eq(productIndex);
    this.fetchProductDetailIntoSlide($slide, this.products[productIndex]);
  }

  // Fetches the product and inserts its HTML into the slide, from the same shared
  // `gives/collections/products/_modal_body` endpoint the collection detail view uses. Both the
  // loading placeholder below (a fixed constant, no interpolated content) and the fetched `html`
  // (server-rendered by our own app from the same-origin fetch above, never third-party or
  // user-authored) are safe to write as markup.
  fetchProductDetailIntoSlide($slide, product) {
    if (!product || $slide.data("loaded")) {
      return;
    }

    $slide.html(LOADING_PRODUCT_DETAIL_HTML);

    const url = this.buildDetailUrl(product);

    fetch(url, RequestHelper.defaultRequestOptions())
      .then((response) => response.text())
      .then((html) => {
        $slide.empty().append(html);
        this.wireProductDetail($slide);
        $slide.data("loaded", true);
      })
      .catch((error) => console.warn("Failed to load product detail into slide", error)); // eslint-disable-line no-console
  }

  wireProductDetail($slide) {
    $slide.find(".thumbs a").on("click", (event) => this.scrollToCorrespondingImg(event));
    $slide.find(".variants select").on("change", (event) => this.changeVariant(event.target.value));
  }

  changeVariant(id) {
    const isDesktop = window.innerWidth > 1130;

    const variant = isDesktop ? "desktop" : "mobile";
    const scrollAttr = isDesktop ? "scrollTop" : "scrollLeft";
    const prop = isDesktop ? "top" : "left";

    const $modal = $(this.modalEl);
    const $thumbnails = $modal.find(`.slick-active .product-details-${variant} .thumbs`);
    const $fullSizeImages = $modal.find(`.slick-active .product-details-${variant} .fullsize`);

    [$thumbnails, $fullSizeImages].forEach((container) => {
      if (!container.length) {
        return;
      }

      const img = container.find(`img[data-id="${id}"]`)[0];

      if (img) {
        container[0][scrollAttr] =
          $(img).offset()[prop] - container.offset()[prop] + container[0][scrollAttr];
      } else {
        container[0][scrollAttr] = 0;
      }
    });

    $thumbnails.find("a").removeClass("active");
    const activeThumb = $thumbnails.find(`img[data-id="${id}"]`)[0];

    if (activeThumb) {
      $(activeThumb).parent().addClass("active");
    } else {
      $thumbnails.find("a:first").addClass("active");
    }
  }

  scrollToCorrespondingImg(event) {
    event.preventDefault();

    const isDesktop = window.innerWidth > 1130;
    const src = $(event.target).attr("src").split("tile")[0];

    const variant = isDesktop ? "desktop" : "mobile";
    const scrollAttr = isDesktop ? "scrollTop" : "scrollLeft";
    const prop = isDesktop ? "top" : "left";

    const $modal = $(this.modalEl);
    const $thumbnails = $modal.find(`.slick-active .product-details-${variant} .thumbs`);
    const $fullSizeImages = $modal.find(`.slick-active .product-details-${variant} .fullsize`);
    const img = $fullSizeImages.find(`img[src^="${src}"]`)[0];

    $thumbnails.find("a").removeClass("active");
    $(event.target).parent().addClass("active");

    if (!$fullSizeImages.length) {
      return;
    }

    if (img) {
      $fullSizeImages[0][scrollAttr] =
        $(img).offset()[prop] - $fullSizeImages.offset()[prop] + $fullSizeImages[0][scrollAttr];
    } else {
      $fullSizeImages[0][scrollAttr] = 0;
    }
  }

  handleScroll() {
    if (!this.desktopGrid) { return; }

    let scrollTop = this.desktopGrid.scrollTop;
    let scrollHeight = this.desktopGrid.scrollHeight;
    let clientHeight = this.desktopGrid.offsetHeight;

    if (scrollTop === 0 && window.scrollY > 0) {
      scrollTop = window.scrollY;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight;
    }

    if (this.loading || this.currentPage >= this.totalPages) { return; }

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      this.loadPage(this.currentCollectionId, this.currentPage + 1);
    }
  }

  refreshSlick() {
    this.element.querySelectorAll(".carousel.slick-initialized").forEach((el) => $(el).slick("refresh"));
  }
}
